// AuthContext: provides authentication state and helpers
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabaseClient';
import { debugLog } from '../lib/debugLog';

const STORAGE_KEY = 'skinny_auth';
const LOCAL_STORAGE_KEYS_TO_CLEAR = ['skinny_auth', 'selected_tenant_id'];
const LOCAL_STORAGE_PREFIXES_TO_CLEAR = ['site_sel_', 'skinny_settings', 'skinny_session_'];

const AuthContext = createContext(null);

function clearSkinnyStorage() {
    if (typeof window === 'undefined') return;
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (!key) continue;
        if (LOCAL_STORAGE_KEYS_TO_CLEAR.includes(key) || LOCAL_STORAGE_PREFIXES_TO_CLEAR.some((prefix) => key.startsWith(prefix))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (_) {
      // ignore storage cleanup failures
    }
  }

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [hasOnboarded, setHasOnboarded] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setUser(parsed.user || null);
        setIsConfigured(Boolean(parsed.isConfigured));
        if (typeof parsed.hasOnboarded === 'boolean') setHasOnboarded(parsed.hasOnboarded);
      }
    } catch (_) {
      // ignore corrupt storage
    }
  }, []);

  useEffect(() => {
    let unsub = () => {};
    (async () => {
      try {
        debugLog('[Auth] getSession start');
        const { data } = await supabase.auth.getSession();
        const authUser = data?.session?.user || null;
        debugLog('[Auth] getSession result', { hasUser: !!authUser });
        setUser(authUser ? { id: authUser.id, email: authUser.email } : null);
      } finally {
        setInitializing(false);
      }
      const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
        const sUser = session?.user || null;
        debugLog('[Auth] onAuthStateChange', { hasUser: !!sUser });
        setUser(sUser ? { id: sUser.id, email: sUser.email } : null);
      });
      unsub = () => sub.subscription.unsubscribe();
    })();
    return () => unsub();
  }, []);

  useEffect(() => {
    const payload = JSON.stringify({ user, isConfigured, hasOnboarded });
    localStorage.setItem(STORAGE_KEY, payload);
  }, [user, isConfigured, hasOnboarded]);

  // Update Sentry user context on auth changes
  useEffect(() => {
    try {
      if (user && user.id) {
        Sentry.setUser({ id: user.id, email: user.email || undefined });
      } else {
        Sentry.setUser(null);
      }
    } catch (_) {
      // ignore sentry context errors
    }
  }, [user]);

  // Auto-onboarding disabled; wizard will trigger onboarding on Finish

  const login = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { ok: false, error };
    const authUser = data.user;
    setUser(authUser ? { id: authUser.id, email: authUser.email } : null);
    return { ok: true };
  }, []);

  const signup = useCallback(async (email, password, nextPath) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(nextPath || '/getting-started')}` },
    });
    if (error) return { ok: false, error };
    const authUser = data?.user || null;
    const session = data?.session || null;

    // Detect duplicate signup: GoTrue returns 200 with no error for existing emails.
    // Two known shapes: (1) identities is empty (confirmed user), or
    // (2) identities present but created_at is well before confirmation_sent_at
    //     (unconfirmed user — GoTrue resends but our SMTP may not deliver).
    if (authUser && !session) {
      const emptyIdentities = Array.isArray(authUser.identities) && authUser.identities.length === 0;
      const created = authUser.created_at ? new Date(authUser.created_at).getTime() : 0;
      const confirmed = authUser.confirmation_sent_at ? new Date(authUser.confirmation_sent_at).getTime() : 0;
      const staleUser = created > 0 && confirmed > 0 && (confirmed - created) > 5000;
      if (emptyIdentities || staleUser) {
        return {
          ok: false,
          error: {
            message: 'An account with this email may already exist. Try logging in, or use "Forgot password" to reset your credentials.',
          },
        };
      }
    }

    // Only set user state if we have an active session (email confirmed)
    // If no session, user needs to confirm email first
    if (session && authUser) {
      setUser({ id: authUser.id, email: authUser.email });
      setIsConfigured(false);
    } else {
      setUser(null);
    }
    
    return { ok: true, session, user: authUser };
  }, []);

  const sendMagicLink = useCallback(async (email, redirectTo) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo || `${window.location.origin}/auth/callback?next=/getting-started` },
    });
    if (error) return { ok: false, error };
    return { ok: true };
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`,
    });
    if (error) return { ok: false, error };
    return { ok: true };
  }, []);

  const updatePassword = useCallback(async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { ok: false, error };
    const authUser = data?.user || null;
    if (authUser) setUser({ id: authUser.id, email: authUser.email });
    return { ok: true };
  }, []);

  const logout = useCallback(async () => {
        await supabase.auth.signOut();
        clearSkinnyStorage();
        try { sessionStorage.removeItem('LINKEDIN_SIGNUP_FIRED'); } catch (_) {}
        setUser(null);
        setIsConfigured(false);
        setHasOnboarded(false);
      }, []);

  const value = useMemo(() => ({
    user,
    initializing,
    isConfigured,
    setIsConfigured,
    login,
    signup,
    sendMagicLink,
    requestPasswordReset,
    updatePassword,
    logout,
  }), [user, initializing, isConfigured, login, signup, sendMagicLink, requestPasswordReset, updatePassword, logout]);

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}


